/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
    Box,
    Button,
    Divider,
    IconButton,
    InputBase,
    MenuItem,
    Paper,
    Stack,
    Typography,
    Select,
} from "@mui/material";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CtaButton from "../../buttonWithArrow";
import PickUpLocation from '../../goole-location/pickupLocation';
import { axiosInstance } from '../../../utils/axiosInstance';
import { useRouter } from "next/navigation";
import SmartDateField from "../../DatePickers/SmartDateField";

// -----------
// ---------- helpers ----------
const pad = (n) => String(n).padStart(2, "0");
const toLocalInputValue = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const addMinutes = (d, mins) => new Date(d.getTime() + mins * 60 * 1000);
const roundToStep = (d, step = 15) => {
    const m = d.getMinutes();
    const rounded = Math.ceil(m / step) * step;
    d.setMinutes(rounded, 0, 0);
    return d;
};

// recomputeable getter for min (now + 4h, snapped to step)
const STEP_MIN = 15;
// const getMinDateTime = () => toLocalInputValue(roundToStep(addMinutes(new Date(), 240), STEP_MIN));

const getMinDateTime = () => toLocalInputValue(addMinutes(new Date(), 240));

function splitDateTime(datetimeStr, label, { addHours = 0, encodeTime = true } = {}) {
    // Accept "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss"
    const [datePart, timePartRaw] = String(datetimeStr).trim().split(/[T\s]/);
    if (!datePart || !timePartRaw) throw new Error("Invalid datetime format (expected YYYY-MM-DDTHH:mm[:ss])");

    const [y, m, d] = datePart.split("-").map(Number);

    // Strip anything after seconds (e.g., timezone suffix) and ensure seconds
    const cleanTime = timePartRaw.replace(/(Z|[+-]\d{2}:?\d{2})$/, "");
    let [hh, mm, ss = "00"] = cleanTime.split(":");
    hh = Number(hh); mm = Number(mm); ss = Number(ss);

    // Build local date
    const dt = new Date(y, m - 1, d, hh, mm, ss);
    if (addHours) dt.setHours(dt.getHours() + addHours); // e.g. addHours = 4

    const pad = (n) => String(n).padStart(2, "0");

    const outDate = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    const outTimeRaw = `${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
    const outTime = encodeTime ? encodeURIComponent(outTimeRaw) : outTimeRaw;

    return String(label).toLowerCase() === "time" ? outTime : outDate;
}



export default function BookRideBar({ hourly }) {
    const router = useRouter();
    const autocompleteFirst = useRef()
    const [pickup, setPickup] = useState("");
    const [drop, setDrop] = useState("");
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [loading, setLoading] = useState(true)
    const [isSubmitClicked, setIsSubmitClicked] = useState(false);
    const [focusNext, setFocusNext] = useState(false)
    const [hourlyValue, setHourlyValue] = useState(null);
    // ---------- state ----------
    const [minDateTime, setMinDateTime] = useState(getMinDateTime());
    // set the picker to the min on mount
    const [dateTime, setDateTime] = useState(minDateTime);
    const [duration, setDuration] = useState('')
    const [loader, setLoader] = useState(false)
    const [pickupLatlng, setPickupLatlng] = useState({
        lat: '',
        lng: ''
    })

    const [dropLatlng, setDropLatlng] = useState({
        lat: '',
        lng: ''
    })
    const [distance, setDistance] = useState('')

    useEffect(() => {
        if (hourly) {
            setHourlyValue(3);
        } else {
            setHourlyValue(null);
        }
    }, [hourly])

    // (optional) keep min moving forward if the page stays open
    useEffect(() => {
        const id = setInterval(() => setMinDateTime(getMinDateTime()), 60_000); // update every minute
        return () => clearInterval(id);
    }, []);

    const isBelowMin = useMemo(
        () => new Date(dateTime) < new Date(minDateTime),
        [dateTime, minDateTime]
    );

    // clamp any user-provided value to >= min
    const clampToMin = (isoLocal) => {
        const picked = new Date(isoLocal);
        const min = new Date(minDateTime);
        if (Number.isNaN(picked.getTime())) return minDateTime;
        return picked < min ? minDateTime : toLocalInputValue(picked);
    };

    const componentDidMountPickup = async () => {
        const response = await axiosInstance.get(
            `${process.env.BACKEND_URL}/location/geocode?address=${pickup}`
        )
        // const response = await axios.get(`/api/geocode?address=${pickup}`)

        const { results } = response.data;

        if (results && results.length > 0) {
            const { lat, lng } = results[0].geometry.location
            setPickupLatlng({
                ...pickupLatlng,
                ['lat']: lat ? lat : 0,
                ['lng']: lng ? lng : 0
            })
        } else {
            setPickupLatlng({
                lat: '',
                lng: ''
            })
        }
    }

    const componentDidMountDrop = async () => {
        const response = await axiosInstance.get(
            `${process.env.BACKEND_URL}/location/geocode?address=${drop}`
        )
        // const response = await axios.get(`/api/geocode?address=${drop}`)

        const { results } = response.data
        if (results && results.length > 0) {
            const { lat, lng } = results[0].geometry.location
            setDropLatlng({
                ...dropLatlng,
                ['lat']: lat ? lat : 0,
                ['lng']: lng ? lng : 0
            })
        } else {
            setDropLatlng({
                lat: '',
                lng: ''
            })
            return null
        }
    }


    const checkValidation = () => {
        if (hourly) {
            return pickup !== '' && pickup !== null
        }
        return pickup !== '' && pickup !== null && drop !== ''

    }

    const handleBookingRide = () => {
        setIsSubmitClicked(true);

        // if (pickup !== '' && pickup !== null && drop !== '') {
        if (checkValidation()) {
            setLoader(true);

            // base query
            const query = {
                pick_up: pickup,
                drop_off: drop,
                pickup_Latlng: JSON.stringify(pickupLatlng),
                drop_Latlng: JSON.stringify(dropLatlng),
                date: splitDateTime(dateTime, "date"),
                time: splitDateTime(dateTime, "time", { encodeTime: false }),
                distance_main: distance,
            };

            // ✅ only add "hourly" if it's defined and not 0 or empty
            if (hourlyValue && Number(hourlyValue) !== 0) {
                query.hourly = hourlyValue;
            }

            // router.push({
            //     pathname: "/carBooking/carBookRide",
            //     query,
            // });
            const queryString = new URLSearchParams(query).toString();
            window.location.replace(`/carBooking/carBookRide?${queryString}`);

        }
    };


    useEffect(() => {
        if (pickup && pickup !== '') {
            componentDidMountPickup()
        }
    }, [pickup])

    useEffect(() => {
        if (drop && drop !== '') {
            componentDidMountDrop()
        }
    }, [drop])

    useEffect(() => {
        if (pickup && drop && pickup !== '' && drop !== '') {
            //   // getRouteDistanceAndDuration(pickup,drop);
            calculateDistanceAndDuration(pickup, drop)
        }
    }, [pickup, drop])

    const calculateDistanceAndDuration = (origin, destination) => {
        const directionsService = new window.google.maps.DirectionsService()

        directionsService.route(
            {
                origin: origin,
                destination: destination,
                travelMode: window.google.maps.TravelMode.DRIVING
            },
            (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                    // setDirections(result);
                    const route = result.routes[0];
                    var temp2 = 0;
                    route.legs.map((item) => {
                        if (item.duration.text.split(' ').length === 2) {
                            temp2 = temp2 + parseInt(item.duration.text.split(' ')[0]);
                        }

                        if (item.duration.text.split(' ').length === 4) {
                            temp2 =
                                temp2 +
                                parseInt(item.duration.text.split(' ')[0]) * 60 +
                                parseInt(item.duration.text.split(' ')[2]);
                        }
                    });
                    // setDuration(result.routes[0].legs[0].duration.text)
                    setDuration(temp2);
                    setDistance(result.routes[0].legs[0].distance.text)
                } else {
                }
            }
        )
    }

    useEffect(() => {
        if (duration) {

            // console.log("duration",duration);

            const durationInHours = Math.ceil(duration / 60);
            const firstEnabled = Array.from({ length: 12 }, (_, i) => i + 3).find((h) => {
                const shouldDisable = duration < 60 ? false : h <= durationInHours;
                return !shouldDisable;
            });
            if (firstEnabled && hourly) setHourlyValue(firstEnabled);
        }
    }, [duration]);

    return (
        <Paper
            elevation={6}
            sx={{
                maxWidth: 1200,
                mx: "auto",
                p: 3,
                borderRadius: "20px",
                bgcolor: "#fff",
            }}
        >
            {/* Header */}
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 22, mb: 1 }}>
                Book Your Ride
            </Typography>

            <Divider orientation="horizontal" flexItem sx={{ borderColor: "#e6eaee", mx: 0.5, mt: 1, mb: 3, opacity: 1 }} />

            {/* Row */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems="end"
            >
                {/* Pickup */}

                <FieldCard label="Pickup Location" hourly={hourly}>
                    <div style={{ width: '100%' }}>
                        <PickUpLocation
                            type={'pickup'}
                            pickup={pickup}
                            newDesign
                            size='small'
                            label='Pickup Location'
                            placeholder='Search a location'
                            setLoading={setLoading}
                            loading={loading}
                            autocompleteFirst={autocompleteFirst}
                            setPickup={mPickUp => {
                                setPickup(mPickUp)
                            }}
                            setDrop={mDrop => {
                                setDrop(mDrop)
                            }}
                            error={
                                (isSubmitClicked && pickup === '') || pickup === null
                                    ? true
                                    : false
                            }
                            helperText={
                                (isSubmitClicked && pickup === '') || pickup === null
                                    ? 'Pickup Location Required'
                                    : ''
                            }
                        />
                    </div>
                    {/* <Stack direction="row" alignItems="center" sx={{ width: "100%" }} spacing={1}>                        
                        <RoomOutlinedIcon fontSize="small" />
                        <InputBase
                            value={pickup}
                            onChange={(e) => setPickup(e.target.value)}
                            placeholder="Search a location"
                            sx={{ flex: 1, fontSize: 14 }}
                        />
                        <ExpandMoreIcon sx={{ opacity: 0.7 }} />
                    </Stack> */}
                </FieldCard>

                {/* Drop */}
                <FieldCard label="Drop Location" hourly={hourly}>
                    <div style={{ width: '100%' }}>
                        <PickUpLocation
                            type={'drop'}
                            size='small'
                            drop={drop}
                            newDesign
                            setPickup={mPickUp => {
                                setPickup(mPickUp)
                            }}
                            label='Drop Location'
                            placeholder='Search a location'
                            setLoading={setLoading}
                            loading={loading}
                            focusNext={focusNext}
                            setDrop={mDrop => {
                                setDrop(mDrop)
                            }}
                            error={isSubmitClicked && !hourly && drop === '' ? true : false}
                            helperText={
                                isSubmitClicked && !hourly && drop === '' ? 'Drop Location Required' : ''
                            }
                        />
                    </div>
                    {/* <Stack direction="row" alignItems="center" sx={{ width: "100%" }} spacing={1}>                        
                        <RoomOutlinedIcon fontSize="small" />                        
                        <InputBase
                            value={drop}
                            onChange={(e) => setDrop(e.target.value)}
                            placeholder="Search a location"
                            sx={{ flex: 1, fontSize: 14 }}
                        />
                        <ExpandMoreIcon sx={{ opacity: 0.7 }} />
                    </Stack> */}
                </FieldCard>

                {hourly && (
                    <FieldCard label="Hours" hourly={hourly}>
                        <Box sx={{ width: '100%' }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                                <Select
                                    fullWidth
                                    size="small"
                                    value={hourlyValue}
                                    onChange={(e) => setHourlyValue(Number(e.target.value))}
                                    variant="standard"
                                    disableUnderline
                                    IconComponent={ExpandMoreIcon}
                                    sx={{
                                        flex: 1, minWidth: 0,
                                        fontSize: 14,
                                        px: 1,
                                        '.MuiSelect-select': { pr: 3, py: 0.5 },
                                    }}
                                    MenuProps={{
                                        PaperProps: { sx: { maxHeight: 300 } },
                                    }}
                                >
                                    {Array.from({ length: 10 }, (_, i) => i + 3).map((h) => {
                                        const durationInHours = Math.ceil(duration / 60);
                                        const shouldDisable = duration < 60 ? false : h <= durationInHours;
                                        return (
                                            <MenuItem key={h} value={h} disabled={shouldDisable}>
                                                {h} {h === 1 ? 'hour' : 'hours'}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </Stack>
                        </Box>
                    </FieldCard>
                )}



                {/* Date + Time */}
                {/* <FieldCard label="Pickup Date">
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                        
                        <CalendarMonthOutlinedIcon fontSize="small" />
                        
                        <InputBase
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            sx={{ fontSize: 14, pr: 1 }}
                        />

                        <Divider orientation="vertical" flexItem />

                        <Select
                            size="small"
                            value={timeOpt}
                            onChange={(e) => setTimeOpt(e.target.value)}
                            variant="standard"
                            disableUnderline
                            IconComponent={ExpandMoreIcon}
                            sx={{
                                fontSize: 14,
                                px: 1,
                                ".MuiSelect-select": { pr: 3, py: 0.5 },
                            }}
                        >
                            <MenuItem value="now">Now</MenuItem>
                            <MenuItem value="15m">In 15 min</MenuItem>
                            <MenuItem value="30m">In 30 min</MenuItem>
                            <MenuItem value="1h">In 1 hour</MenuItem>
                        </Select>
                    </Stack>
                </FieldCard> */}


                <FieldCard label="Pickup Date & Time" hourly={hourly}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                        {!hourly && <CalendarMonthOutlinedIcon fontSize="small" />}
                        <SmartDateField
                            value={dateTime}
                            setValue={setDateTime}
                            minDateTime={minDateTime}   // ← use T
                        />
                        {/* <InputBase
                            type="datetime-local"
                            value={dateTime}
                            onChange={(e) => setDateTime(clampToMin(e.target.value))}
                            inputProps={{
                                min: minDateTime,        // hard limit in the native picker UI
                                step: 900,               // 15-minute increments (optional)
                                onInvalid: (e) => {
                                    e.target.setCustomValidity(
                                        `Pick a time ≥ ${minDateTime.replace('T', ' ')}`
                                    );
                                },
                                onInput: (e) => {
                                    // clear the message once it's valid/changed
                                    e.target.setCustomValidity('');
                                },
                            }}
                            sx={{ fontSize: 14, pr: 1, width: '100%' }}
                        /> */}

                        {/* {isBelowMin && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                Pick a time at least 4 hours from now.
                            </Typography>
                        )} */}

                    </Stack>
                </FieldCard>

                {/* CTA */}
                {/* <CtaButton cta={{ label: "Book Now", href: "#" }} /> */}
                <CtaButton label="Book Now" onClick={() => handleBookingRide()} loader={loader} />
            </Stack>
        </Paper>
    );
}

/** Small helper: field container with label (matches your pill style) */
function FieldCard({ label, children, hourly }) {
    return (
        <Box sx={{ flex: 1, minWidth: { xs: "100%", md: hourly ? 150 : 260 } }}>
            <Typography
                component="label"
                sx={{
                    fontWeight: 600,
                    fontSize: "17px",
                    lineHeight: "100%",
                    letterSpacing: "0",
                    color: "#111",
                    mb: 0.75,
                    display: "block",
                }}
            >
                {label}
            </Typography>

            <Paper
                variant="outlined"
                sx={{
                    height: 56,
                    px: 1.25,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "10px",
                    borderColor: "#e6eaee",
                    bgcolor: "#f5f7f8",
                }}
            >
                {children}
            </Paper>
        </Box>
    );
}

/** Icon chip at field left */
function IconChip({ children }) {
    return (
        <IconButton
            size="small"
            disableRipple
            sx={{
                p: 0,
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: "#fff",
                border: "1px solid #e6eaee",
                display: "grid",
                placeItems: "center",
            }}
        >
            {children}
        </IconButton>
    );
}
